import type {
  DpiaContext,
  DpiaContextDTO,
  DpiaDetail,
  DpiaDetailDTO,
  DpiaDetailResponse,
  DpiaDetailResponseDTO,
  DpiaMeasure,
  DpiaMeasureDTO,
  DpiaRisk,
  DpiaRiskDTO,
  UpdateDpiaInput,
} from './dpias.types';

export const parseMeasureForFE = (dto: DpiaMeasureDTO): DpiaMeasure => ({
  id: dto.id,
  dpiaRiskId: dto.dpia_risk_id,
  title: dto.title,
  description: dto.description,
  responsibleMembershipId: dto.responsible_membership_id,
  responsibleName: dto.responsible_name,
  dueDate: dto.due_date,
  status: dto.status,
  sortOrder: dto.sort_order,
});

export const parseRiskForFE = (dto: DpiaRiskDTO): DpiaRisk => ({
  id: dto.id,
  title: dto.title,
  description: dto.description,
  affectedRightsAndFreedoms: dto.affected_rights_and_freedoms,
  likelihood: dto.likelihood,
  impact: dto.impact,
  riskLevel: dto.risk_level,
  measures: dto.measures.map(parseMeasureForFE),
  sortOrder: dto.sort_order,
});

export const parseDpiaDetailForFE = (dto: DpiaDetailDTO): DpiaDetail => ({
  id: dto.id,
  organizationId: dto.organization_id,
  assetProcessingActivityId: dto.asset_processing_activity_id,
  sourceDpiaRequestId: dto.source_dpia_request_id,
  title: dto.title,
  status: dto.status,
  responsibleMembershipId: dto.responsible_membership_id,
  responsibleName: dto.responsible_name,
  initiatedAt: dto.initiated_at,
  plannedProcessingDescription: dto.planned_processing_description,
  assessmentScope: dto.assessment_scope,
  projectOrChangeContext: dto.project_or_change_context,
  selectedPurposeIds: dto.selected_purpose_ids,
  necessityAssessment: dto.necessity_assessment,
  proportionalityAssessment: dto.proportionality_assessment,
  dataMinimisationAssessment: dto.data_minimisation_assessment,
  retentionAndAccessAssessment: dto.retention_and_access_assessment,
  overallAssessment: dto.overall_assessment,
  residualRiskLevel: dto.residual_risk_level,
  priorConsultationAssessment: dto.prior_consultation_assessment,
  ownerConclusion: dto.owner_conclusion,
  administratorClarificationComment: dto.administrator_clarification_comment,
  ownerResponseComment: dto.owner_response_comment,
  revision: dto.revision,
  submittedRevision: dto.submitted_revision,
  submittedAt: dto.submitted_at,
  submittedByMembershipId: dto.submitted_by_membership_id,
  submittedByName: dto.submitted_by_name,
  updatedAt: dto.updated_at,
  updatedByMembershipId: dto.updated_by_membership_id,
  updatedByName: dto.updated_by_name,
  reviewDecisionComment: dto.review_decision_comment,
  approvedAt: dto.approved_at,
  approvedByMembershipId: dto.approved_by_membership_id,
  approvedByName: dto.approved_by_name,
  approvedRevision: dto.approved_revision,
  clarificationRequestedAt: dto.clarification_requested_at,
  clarificationRequestedByMembershipId: dto.clarification_requested_by_membership_id,
  clarificationRequestedByName: dto.clarification_requested_by_name,
  risks: dto.risks.map(parseRiskForFE),
  canEdit: dto.can_edit,
});

export const parseDpiaContextForFE = (dto: DpiaContextDTO): DpiaContext => ({
  assetName: dto.asset_name,
  assetTypeName: dto.asset_type_name,
  assetProcessingActivityName: dto.asset_processing_activity_name,
  operationalOwnerName: dto.operational_owner_name,
  dataSubjectCategories: dto.data_subject_categories,
  personalDataCategories: dto.personal_data_categories,
  countryHandling: dto.country_handling,
  minimumSecurityAssessment: dto.minimum_security_assessment,
  availablePurposes: dto.available_purposes.map(purpose => ({
    id: purpose.id,
    assetProcessingPurposeId: purpose.asset_processing_purpose_id,
    name: purpose.name,
    legalBasis: purpose.legal_basis,
    applicationDescription: purpose.application_description,
  })),
  sourceRequest: {
    recommendation: dto.source_request.recommendation,
    decision: dto.source_request.decision,
    decisionComment: dto.source_request.decision_comment,
  },
});

export const parseDpiaDetailResponseForFE = (dto: DpiaDetailResponseDTO): DpiaDetailResponse => ({
  dpia: parseDpiaDetailForFE(dto.dpia),
  context: parseDpiaContextForFE(dto.context),
});

export const parseMeasureForBE = (measure: Partial<Omit<DpiaMeasure, 'responsibleName'>>) => ({
  ...(measure.id !== undefined ? { id: measure.id } : {}),
  ...(measure.dpiaRiskId !== undefined ? { dpia_risk_id: measure.dpiaRiskId } : {}),
  ...(measure.title !== undefined ? { title: measure.title.trim() } : {}),
  ...(measure.description !== undefined ? { description: measure.description.trim() } : {}),
  ...(measure.responsibleMembershipId !== undefined ? { responsible_membership_id: measure.responsibleMembershipId } : {}),
  ...(measure.status !== undefined ? { status: measure.status } : {}),
  ...(measure.dueDate !== undefined ? { due_date: measure.dueDate } : {}),
  ...(measure.sortOrder !== undefined ? { sort_order: measure.sortOrder } : {}),
});

export const parseRiskForBE = (risk: Partial<Omit<DpiaRisk, 'riskLevel' | 'measures'>>) => ({
  ...(risk.id !== undefined ? { id: risk.id } : {}),
  ...(risk.title !== undefined ? { title: risk.title.trim() } : {}),
  ...(risk.description !== undefined ? { description: risk.description.trim() } : {}),
  ...(risk.affectedRightsAndFreedoms !== undefined ? { affected_rights_and_freedoms: risk.affectedRightsAndFreedoms.trim() } : {}),
  ...(risk.likelihood !== undefined ? { likelihood: risk.likelihood } : {}),
  ...(risk.impact !== undefined ? { impact: risk.impact } : {}),
  ...(risk.sortOrder !== undefined ? { sort_order: risk.sortOrder } : {}),
});

export const parseUpdatePurposeScopeForBE = (payload: { selectedPurposeIds: number[] }) => ({
  selected_purpose_ids: payload.selectedPurposeIds,
});

export const parseUpdateDpiaForBE = (payload: UpdateDpiaInput) => ({
  revision: payload.revision,
  planned_processing_description: payload.plannedProcessingDescription.trim(),
  assessment_scope: payload.assessmentScope.trim(),
  project_or_change_context: payload.projectOrChangeContext.trim(),
  selected_purpose_ids: payload.selectedPurposeIds,
  necessity_assessment: payload.necessityAssessment.trim(),
  proportionality_assessment: payload.proportionalityAssessment.trim(),
  data_minimisation_assessment: payload.dataMinimisationAssessment.trim(),
  retention_and_access_assessment: payload.retentionAndAccessAssessment.trim(),
  overall_assessment: payload.overallAssessment.trim(),
  residual_risk_level: payload.residualRiskLevel,
  prior_consultation_assessment: payload.priorConsultationAssessment,
  owner_conclusion: payload.ownerConclusion.trim(),
  owner_response_comment: payload.ownerResponseComment.trim(),
});

export const parseSubmitDpiaForBE = (revision: number) => ({
  revision,
});

export const parseApproveDpiaForBE = (payload: { submittedRevision: number; reviewDecisionComment: string }) => ({
  submitted_revision: payload.submittedRevision,
  review_decision_comment: payload.reviewDecisionComment.trim(),
});

export const parseRequestDpiaClarificationForBE = (payload: { submittedRevision: number; administratorClarificationComment: string }) => ({
  submitted_revision: payload.submittedRevision,
  administrator_clarification_comment: payload.administratorClarificationComment.trim(),
});
